import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';

const workSteps = [
  {
    icon: 'work-icon-01.svg',
    title: 'Step 1 - Discovery and Workflow Mapping',
    desc: 'We align on outcomes, map real workflows, identify bottlenecks, and define measurable acceptance criteria and edge cases.',
  },
  {
    icon: 'work-icon-02.svg',
    title: 'Step 2 - Architecture and Data Design',
    desc: 'We design domain models, APIs, integrations, and data boundaries with security, auditability, and performance built in.',
  },
  {
    icon: 'work-icon-03.svg',
    title: 'Step 3 - Iterative Build and Integration',
    desc: 'We deliver in short sprints, integrate with your systems early, and validate continuously with stakeholders and real test data.',
  },
  {
    icon: 'work-icon-04.svg',
    title: 'Step 4 - Hardening, QA, and Release',
    desc: 'We run role-based access tests, integration tests, and performance checks, then deploy with staging, monitoring, and rollback plans.',
  },
  {
    icon: 'work-icon-05.svg',
    title: 'Step 5 - Adoption and Continuous Improvement',
    desc: 'Post-launch, we refine workflows based on usage, tighten reliability and cost, and ship enhancements without disrupting operations.',
  },
];

const faqItems = [
  {
    question: 'What kinds of enterprise applications do you build?',
    answer:
      'We build internal portals, dashboards, workflow systems, approvals and compliance tooling, customer-facing platforms, and integration-heavy business applications. Typical examples include operational CRMs, order and case management, service portals, billing and reporting layers, and custom admin platforms.',
  },
  {
    question: 'Can you integrate with our existing systems and data sources?',
    answer:
      'Yes. We commonly integrate with CRMs and ERPs (Salesforce, HubSpot, SAP, Microsoft Dynamics, NetSuite), identity providers (Google, Microsoft, Okta), support systems, payment gateways, and data warehouses. We design integrations with retries, idempotency, and audit logs to reduce operational failures.',
  },
  {
    question: 'How do you handle security and access control?',
    answer:
      'Security is built into the architecture. We implement role-based access control with object-level authorization, secure API design, audit trails for sensitive actions, and environment separation (dev, staging, production). Where required, we support SSO and structured access reviews aligned to internal policies.',
  },
  {
    question: 'Do you modernize or re-engineer legacy software?',
    answer:
      'Yes. We modernize legacy systems by improving architecture, refactoring risky modules, migrating data safely, and introducing observability and automation. We typically use staged migration patterns to preserve uptime and reduce risk while moving to a more maintainable foundation.',
  },
  {
    question: 'How do you ensure the system remains maintainable as it grows?',
    answer:
      'We use modular architecture, clear domain boundaries, consistent coding standards, automated tests for critical workflows, and production-grade observability (logs, metrics, alerting). This reduces future change cost and prevents the system from becoming fragile as new requirements emerge.',
  },
  {
    question: 'What does a typical delivery timeline look like?',
    answer:
      'Timelines depend on scope and integration complexity. Many teams start with a focused release in 4 to 8 weeks (core workflows, roles, dashboards), then expand in phases. We plan delivery around measurable outcomes and adoption milestones, not only feature count.',
  },
];

const ServiceCustomSoftware = () => {
  useEffect(() => {
    if (window.WOW) new window.WOW().init();
    if (window.initMagnetic) window.initMagnetic();
  }, []);

  return (
    <>
      <section className="breadcrumbs">
        <div className="container">
          <div className="row">
            <div className="col-12">
              <div className="breadcrumb-wrapper">
                <div className="breadcrumb-cnt">
                  <span>Services Details</span>
                  <h1>Custom Software and Enterprise Applications</h1>
                  <div className="breadcrumb-list">
                    <Link to="/">Home</Link>
                    <img src="/assets/img/content/breadcrumb-arrow.svg" alt="" />
                    Services Details
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="service-details">
        <div className="about-services sec-mar">
          <div className="container">
            <div className="row gy-5">
              <div
                className="col-lg-6 d-flex align-items-center wow animate fadeInLeft"
                data-wow-delay="200ms"
                data-wow-duration="1500ms"
              >
                <div className="about-content">
                  <div className="section-title-5">
                    <h2>
                      Custom Software <br /> and Enterprise Applications
                    </h2>
                  </div>

                  <p>
                    We build business-critical applications that improve execution, visibility, and control across your operations.
                    This includes internal platforms, customer portals, workflow systems, and integration-heavy tools that unify data
                    and reduce manual handoffs. Our delivery approach prioritizes maintainability, security, and measurable outcomes
                    so the software continues to work as teams, processes, and compliance requirements evolve.
                  </p>

                  <p>
                    You get more than screens and features. We engineer a dependable foundation: clear roles and permissions,
                    audit-ready workflows, resilient integrations, and observability that helps teams diagnose issues quickly and
                    ship changes safely.
                  </p>

                  <ul className="about-featue">
                    <li>
                      <svg width="15" height="15">
                        <circle cx="7" cy="7" r="7" fill="#c79528" />
                      </svg>
                      Operational portals, dashboards, and internal tools
                    </li>
                    <li>
                      <svg width="15" height="15">
                        <circle cx="7" cy="7" r="7" fill="#c79528" />
                      </svg>
                      Workflow systems with approvals and escalations
                    </li>
                    <li>
                      <svg width="15" height="15">
                        <circle cx="7" cy="7" r="7" fill="#c79528" />
                      </svg>
                      CRM/ERP integration and API-first architecture
                    </li>
                    <li>
                      <svg width="15" height="15">
                        <circle cx="7" cy="7" r="7" fill="#c79528" />
                      </svg>
                      Role-based access control and audit trails
                    </li>
                    <li>
                      <svg width="15" height="15">
                        <circle cx="7" cy="7" r="7" fill="#c79528" />
                      </svg>
                      Reporting readiness and data integrity controls
                    </li>
                    <li>
                      <svg width="15" height="15">
                        <circle cx="7" cy="7" r="7" fill="#c79528" />
                      </svg>
                      Legacy modernization and incremental migration
                    </li>
                  </ul>
                </div>
              </div>

              <div
                className="col-lg-6 wow animate fadeInRight"
                data-wow-delay="300ms"
                data-wow-duration="1500ms"
              >
                <div className="service-img magnetic-item">
                  <img
                    className="img-fluid"
                    src="/assets/img/services/laptop.png"
                    alt="Service"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="how-we-work-section sec-mar">
          <div className="container">
            <div
              className="row mb-60 wow animate fadeInUp"
              data-wow-delay="200ms"
              data-wow-duration="1500ms"
            >
              <div className="col-lg-12">
                <div className="section-title">
                  <h2>How We Work</h2>
                </div>
              </div>
            </div>
          </div>

          <div className="work-item">
            <div className="container-fluid">
              <div className="row row-cols-xl-5 row-cols-lg-4 row-cols-md-3 row-cols-sm-2 row-cols-1 gy-5 justify-content-center">
                {workSteps.map((step, idx) => (
                  <div
                    key={step.title}
                    className="col magnetic-item wow animate fadeInUp"
                    data-wow-delay={`${200 + idx * 100}ms`}
                    data-wow-duration="1500ms"
                  >
                    <div className="single-work">
                      <div className="work-icon">
                        <img src={`/assets/img/content/${step.icon}`} alt="" />
                      </div>
                      <div className="work-content">
                        <h3>{step.title}</h3>
                        <p>{step.desc}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="service-sort-driscription-area sec-mar">
          <div className="container">
            <div className="row">
              <div
                className="col-lg-12 wow animate fadeInUp"
                data-wow-delay="200ms"
                data-wow-duration="1500ms"
              >
                <div className="driscription-img magnetic-item">
                  <img
                    className="img-fluid"
                    src="/assets/img/services/rectangular_image.png"
                    alt="Description"
                  />
                </div>
              </div>

              <div className="driscription-content">
                <div className="row">
                  <div
                    className="col-md-6 wow animate fadeInUp"
                    data-wow-delay="200ms"
                    data-wow-duration="1500ms"
                  >
                    <p>
                      Enterprise systems succeed when workflows are explicit, data is reliable, and change is safe. We design software
                      around stable domain concepts and clear boundaries, then implement the operational essentials that teams usually
                      discover too late: object-level authorization, audit events, integration failure handling, and production-grade
                      monitoring.
                    </p>

                    <ul className="about-featue">
                      <li>
                        <svg width="15" height="15">
                          <circle cx="7" cy="7" r="7" fill="#c79528" />
                        </svg>
                        API-first design with versioning and idempotency
                      </li>
                      <li>
                        <svg width="15" height="15">
                          <circle cx="7" cy="7" r="7" fill="#c79528" />
                        </svg>
                        Background jobs for imports, exports, and processing
                      </li>
                      <li>
                        <svg width="15" height="15">
                          <circle cx="7" cy="7" r="7" fill="#c79528" />
                        </svg>
                        Observability: logs, metrics, alerts, and dashboards
                      </li>
                    </ul>
                  </div>

                  <div
                    className="col-md-6 wow animate fadeInUp"
                    data-wow-delay="300ms"
                    data-wow_DURATION="1500ms"
                  >
                    <p>
                      We also adapt delivery to your industry realities. For regulated domains, we prioritize auditability and access
                      control. For high-volume operations, we prioritize performance, queueing, and data consistency. For integration-
                      heavy environments, we design reliable sync and event flows that reduce manual reconciliation.
                    </p>

                    <ul className="about-featue">
                      <li>
                        <svg width="15" height="15">
                          <circle cx="7" cy="7" r="7" fill="#c79528" />
                        </svg>
                        Healthcare: intake workflows, permissions, audit trails
                      </li>
                      <li>
                        <svg width="15" height="15">
                          <circle cx="7" cy="7" r="7" fill="#c79528" />
                        </svg>
                        Fintech: reconciliation, approvals, traceable changes
                      </li>
                      <li>
                        <svg width="15" height="15">
                          <circle cx="7" cy="7" r="7" fill="#c79528" />
                        </svg>
                        Logistics and retail: order and inventory visibility
                      </li>
                      <li>
                        <svg width="15" height="15">
                          <circle cx="7" cy="7" r="7" fill="#c79528" />
                        </svg>
                        Manufacturing: asset tracking, QA workflows, reporting
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="service-faq-area sec-mar">
          <div className="container">
            <div className="row gy-5">
              <div
                className="col-lg-6 wow animate fadeInLeft"
                data-wow-delay="200ms"
                data-wow-duration="1500ms"
              >
                <div className="faq-img magnetic-item">
                  <img
                    className="img-fluid"
                    src="/assets/img/services/faqs.png"
                    alt="FAQ"
                  />
                </div>
              </div>

              <div
                className="col-lg-6 d-flex align-items-center wow animate.fadeInRight"
                data-wow-delay="300ms"
                data-wow_DURATION="1500ms"
              >
                <div className="faq-content-area">
                  <h2>Frequently Asked Questions</h2>

                  <div className="accordion" id="accordionExample">
                    {faqItems.map((item, index) => (
                      <div key={item.question} className="accordion-item">
                        <h6 className="accordion-header" id={`heading${index}`}>
                          <button
                            className={`accordion-button ${index !== 0 ? 'collapsed' : ''}`}
                            type="button"
                            data-bs-toggle="collapse"
                            data-bs-target={`#collapse${index}`}
                            aria-expanded={index === 0 ? 'true' : 'false'}
                            aria-controls={`collapse${index}`}
                          >
                            {item.question}
                          </button>
                        </h6>
                        <div
                          id={`collapse${index}`}
                          className={`accordion-collapse collapse ${index === 0 ? 'show' : ''}`}
                          aria-labelledby={`heading${index}`}
                          data-bs-parent="#accordionExample"
                        >
                          <div className="accordion-body">{item.answer}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ServiceCustomSoftware;



